import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  InlineMessage,
  Loader,
  Typography,
} from '@saviynt/design-system';

import {
  downloadPreLaunchReport,
  getPreLaunchReport,
  getPreLaunchReportHistory,
  getPreLaunchStatus,
  launchFromPreLaunch,
  previewFromPreLaunch,
  resetPreLaunch,
  retriggerPreLaunchAnalysis,
  setPreLaunchCampaignId,
  triggerPreLaunchAnalysis,
} from '../../redux/prelaunch/action';
import { useInvertedLocalize } from '../../utils/localization';

import EmptyState from './components/EmptyState';
import LaunchConfirmModal from './components/LaunchConfirmModal';
import PageFooter from './components/PageFooter';
import PageHeader from './components/PageHeader';
import invertedMessages from './messages';
import PreLaunchDashboard from './PreLaunchDashboard';
import PreLaunchDetail from './PreLaunchDetail';

import './CampaignPreLaunch.css';

const POLL_INTERVAL_MS = 10000;

function CampaignPreLaunch() {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const l = useInvertedLocalize(invertedMessages);

  const queryParams = new URLSearchParams(location.search);
  const campaignIdParam = queryParams.get('campaignId');

  const {
    campaignId,
    statusData,
    isTriggerLoading,
    triggerResult,
    triggerError,
    triggerConflict,
    featureDisabled,
    forbidden,
    isHistoryLoading,
    historyData,
    reportData,
    isReportLoading,
    isDownloadLoading,
  } = useSelector((state) => state.certifications.prelaunch);

  const [detailInfo, setDetailInfo] = useState(null);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPagination, setDetailPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
  });
  const [launchModal, setLaunchModal] = useState({
    isOpen: false,
    blockerCount: 0,
    blockerDetails: [],
  });
  const [actionError, setActionError] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isLaunchLoading, setIsLaunchLoading] = useState(false);
  const pollIntervalRef = useRef(null);

  const cId = campaignId || campaignIdParam;

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (cIdArg, rId) => {
      stopPolling();
      pollIntervalRef.current = setInterval(() => {
        dispatch(getPreLaunchStatus({ campaignId: cIdArg, reportId: rId }));
      }, POLL_INTERVAL_MS);
    },
    [dispatch, stopPolling]
  );

  useEffect(() => {
    if (campaignIdParam) {
      dispatch(setPreLaunchCampaignId(campaignIdParam));
      dispatch(getPreLaunchReportHistory(campaignIdParam));
    }

    return () => {
      stopPolling();
      dispatch(resetPreLaunch());
    };
  }, [campaignIdParam]); // eslint-disable-line

  useEffect(() => {
    if (isHistoryLoading || !historyData) return;
    if (historyData.length === 0) return;

    const latest = historyData[0];

    if (latest.status === 'COMPLETED') {
      dispatch(getPreLaunchReport({ reportId: latest.reportId }));
    } else if (latest.status === 'RUNNING') {
      dispatch(
        getPreLaunchStatus({ campaignId: cId, reportId: latest.reportId })
      );
      startPolling(cId, latest.reportId);
    } else if (latest.status === 'FAILED') {
      dispatch(
        getPreLaunchStatus({ campaignId: cId, reportId: latest.reportId })
      );
    }
  }, [isHistoryLoading, historyData]); // eslint-disable-line

  useEffect(() => {
    if (!statusData) return;

    if (statusData.status === 'RUNNING') {
      startPolling(cId, statusData.reportId);
    }

    if (statusData.status === 'COMPLETED') {
      stopPolling();
      dispatch(getPreLaunchReport({ reportId: statusData.reportId }));
      dispatch(getPreLaunchReportHistory(cId));
    }

    if (statusData.status === 'FAILED') {
      stopPolling();
    }
  }, [statusData?.status, statusData?.reportId]); // eslint-disable-line

  useEffect(() => {
    if (triggerResult?.reportId) startPolling(cId, triggerResult.reportId);
  }, [triggerResult?.reportId]); // eslint-disable-line

  // ── Action handlers ──────────────────────────────────────────────────────
  const handleTrigger = () => {
    if (cId) dispatch(triggerPreLaunchAnalysis(cId));
  };

  const handleDownload = () => {
    if (reportData?.reportId)
      dispatch(downloadPreLaunchReport(reportData.reportId));
  };

  const getFailedBlockers = () => {
    if (!reportData) return [];

    let blockers = [];

    try {
      blockers = JSON.parse(reportData.blockersJson || '[]');
    } catch {
      /* ignore */
    }

    return blockers.filter((b) => b.status === 'FAIL');
  };

  const showBlockerModal = (failedBlockers) => {
    const details = failedBlockers.map((b) => ({
      code: b.code || b.id || '',
      description: b.description || b.message || '',
    }));

    setLaunchModal({
      isOpen: true,
      blockerCount: failedBlockers.length,
      blockerDetails: details,
    });
  };

  const handlePreview = () => {
    const failedBlockers = getFailedBlockers();

    if (failedBlockers.length > 0) {
      showBlockerModal(failedBlockers);

      return;
    }

    setActionError(null);
    setIsPreviewLoading(true);
    dispatch(previewFromPreLaunch(cId))
      .then((res) => {
        if (res?.success === false) {
          setActionError(res?.error?.message || 'Preview failed.');
          setIsPreviewLoading(false);

          return;
        }

        window.location = '/ECM/campaign/list';
      })
      .catch(() => {
        setActionError('Preview failed. Please try again.');
        setIsPreviewLoading(false);
      });
  };

  const handleLaunch = () => {
    const failedBlockers = getFailedBlockers();

    if (failedBlockers.length > 0) {
      showBlockerModal(failedBlockers);

      return;
    }

    setActionError(null);
    setIsLaunchLoading(true);
    dispatch(launchFromPreLaunch(cId))
      .then((res) => {
        if (res?.success === false) {
          setActionError(res?.error?.message || 'Launch failed.');
          setIsLaunchLoading(false);

          return;
        }

        window.location = '/ECM/campaign/list';
      })
      .catch(() => {
        setActionError('Launch failed. Please try again.');
        setIsLaunchLoading(false);
      });
  };

  const handleLaunchModalClose = () => {
    setLaunchModal({ isOpen: false, blockerCount: 0, blockerDetails: [] });
  };

  const handleClose = () => history.goBack();

  const handleOpenDetail = (metric, label) => {
    setDetailInfo({ metric, label });
    setDetailPage(1);
    setDetailPagination({ currentPage: 1, totalPages: 0, totalCount: 0 });
  };

  const handleCloseDetail = () => {
    setDetailInfo(null);
    setDetailPage(1);
    setDetailPagination({ currentPage: 1, totalPages: 0, totalCount: 0 });
  };

  const handleRetrigger = () => {
    if (cId) {
      dispatch(retriggerPreLaunchAnalysis(cId));
      window.location = '/ECM/campaign/list';
    }
  };

  const handleDetailPageChange = (p) => setDetailPage(p);
  const handleDetailPaginationReady = (pagination) =>
    setDetailPagination(pagination);

  const currentStatus = statusData?.status || reportData?.status || '';

  // ── No campaign ID ────────────────────────────────────────────────────────
  if (!cId) {
    return (
      <Box className='CampaignPreLaunch'>
        <Box className='CampaignPreLaunch-body CampaignPreLaunch-loading'>
          {l(
            'No campaign ID provided. Please navigate here from a campaign record.'
          )}
        </Box>
      </Box>
    );
  }

  // ── Feature disabled ──────────────────────────────────────────────────────
  if (featureDisabled) {
    return (
      <Box className='CampaignPreLaunch'>
        <Box className='CampaignPreLaunch-body CampaignPreLaunch-empty'>
          {l(
            'Pre-Launch Analysis is disabled. Enable it in EcmConfig (PRELAUNCH_ENABLED=true) to use this feature.'
          )}
        </Box>
      </Box>
    );
  }

  // ── Forbidden ─────────────────────────────────────────────────────────────
  if (forbidden) {
    return (
      <Box className='CampaignPreLaunch'>
        <Box className='CampaignPreLaunch-body CampaignPreLaunch-empty'>
          {l(
            'You do not have permission to run Pre-Launch Analysis. Required role: Campaign Admin.'
          )}
        </Box>
      </Box>
    );
  }

  // ── Initial loading ───────────────────────────────────────────────────────
  if (isHistoryLoading && (!historyData || historyData.length === 0)) {
    return (
      <Box className='CampaignPreLaunch'>
        <Box className='CampaignPreLaunch-body CampaignPreLaunch-loading'>
          <Loader kind='dots' format='inline' size='medium' color='brand' />
          {l('Loading pre-launch analytics…')}
        </Box>
      </Box>
    );
  }

  // ── No runs yet — show Start Analysis ────────────────────────────────────
  if (!historyData || historyData.length === 0) {
    return (
      <Box className='CampaignPreLaunch'>
        <Box className='PageHeader'>
          <Box className='PageHeader-titleGroup'>
            <Typography
              kind='h2'
              desktop='h2-d'
              mobile='h2-d'
              className='PageHeader-title'>
              {l('Pre-Launch Analytics')}
            </Typography>
            <Box className='PageHeader-subtitle'>
              <Box tag='span' className='PageHeader-campaignName'>
                {`${l('Campaign')} `}
                {String(cId).replace(/[^a-zA-Z0-9_-]/g, '')}
              </Box>
            </Box>
          </Box>
          <ButtonIcon
            kind='ghost'
            size='medium'
            icon={
              <Icon
                kind='Close'
                size='smallMedium'
                color='--color-foreground-neutral-subtle'
              />
            }
            onClick={handleClose}
            ariaLabel={l('close')}
            className='PageHeader-closeBtn'
          />
        </Box>

        <EmptyState
          triggerError={triggerError}
          triggerConflict={triggerConflict}
          isTriggerLoading={isTriggerLoading}
          onTrigger={handleTrigger}
          onClose={handleClose}
        />
      </Box>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <Box className='CampaignPreLaunch'>
      <PageHeader
        reportData={reportData}
        currentStatus={currentStatus}
        onClose={handleClose}
      />

      {/* ── Notice banner — shown when report data is available ──────────────── */}
      {reportData && (
        <InlineMessage
          kind='background'
          colorTheme='warning'
          size='medium'
          leftIcon={<Icon kind='AlertWarningFilled' size='small' />}
          text={l(
            'These results reflect your data as of {timestamp}.\nPre-Launch Analytics does not guarantee final campaign scope — data changes between this analysis and launch may cause variances.\nRe-run analysis before launching to reflect the latest data quality insights and other metrics.',
            {
              timestamp: reportData.completedAt
                ? new Date(reportData.completedAt).toLocaleString()
                : l('the analysis run time'),
            }
          )}
          className='CampaignPreLaunch-notice'
        />
      )}

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <Box
        className={`CampaignPreLaunch-body${
          detailInfo ? ' CampaignPreLaunch-body--noScroll' : ''
        }`}>
        {detailInfo ? (
          <Box id='plDetailContainer' className='PreLaunchDetail'>
            <Box className='PreLaunchDetail-header'>
              <Button
                type='button'
                kind='outlined'
                size='medium'
                leftIcon={<Icon kind='ArrowLeft' size='xSmall' />}
                onClick={handleCloseDetail}>
                {l('Back to dashboard')}
              </Button>
              <Typography kind='h5' className='PreLaunchDetail-title'>
                {detailInfo.label}
              </Typography>
            </Box>
            <PreLaunchDetail
              metric={detailInfo.metric}
              page={detailPage}
              onPaginationReady={handleDetailPaginationReady}
            />
          </Box>
        ) : (
          <PreLaunchDashboard onDrillDown={handleOpenDetail} />
        )}
      </Box>

      {/* ── Detail pagination — fixed above footer, only in detail view ── */}
      {detailInfo && detailPagination.totalPages > 0 && (
        <Box className='CampaignPreLaunch-detailPagination'>
          <Box tag='span' className='CampaignPreLaunch-detailPageInfo'>
            {detailPagination.totalPages > 1
              ? `Page ${detailPagination.currentPage} of ${
                  detailPagination.totalPages
                } (${detailPagination.totalCount.toLocaleString()} records)`
              : `${detailPagination.totalCount.toLocaleString()} record${
                  detailPagination.totalCount === 1 ? '' : 's'
                }`}
          </Box>
          {(() => {
            const { currentPage: cp, totalPages: tp } = detailPagination;
            const windowSize = 5;
            const half = Math.floor(windowSize / 2);
            let start = Math.max(1, cp - half);
            let end = start + windowSize - 1;

            if (end > tp) {
              end = tp;
              start = Math.max(1, end - windowSize + 1);
            }

            const pages = [];

            for (let i = start; i <= end; i += 1) pages.push(i);

            return (
              <>
                <Button
                  type='button'
                  kind='outlined'
                  size='small'
                  isDisabled={cp === 1}
                  onClick={() => handleDetailPageChange(1)}>
                  {l('First')}
                </Button>
                <Button
                  type='button'
                  kind='outlined'
                  size='small'
                  isDisabled={cp === 1}
                  onClick={() => handleDetailPageChange(cp - 1)}>
                  {l('Prev')}
                </Button>
                {pages.map((p) => (
                  <Button
                    key={p}
                    type='button'
                    kind={p === cp ? 'filled' : 'outlined'}
                    size='small'
                    onClick={() => handleDetailPageChange(p)}>
                    {p}
                  </Button>
                ))}
                <Button
                  type='button'
                  kind='outlined'
                  size='small'
                  isDisabled={cp === tp}
                  onClick={() => handleDetailPageChange(cp + 1)}>
                  {l('Next')}
                </Button>
                <Button
                  type='button'
                  kind='outlined'
                  size='small'
                  isDisabled={cp === tp}
                  onClick={() => handleDetailPageChange(tp)}>
                  {l('Last')}
                </Button>
              </>
            );
          })()}
        </Box>
      )}

      {actionError && (
        <InlineMessage
          kind='background'
          colorTheme='critical'
          size='medium'
          leftIcon={<Icon kind='AlertCriticalFilled' size='small' />}
          text={actionError}
          className='CampaignPreLaunch-actionError'
        />
      )}

      <PageFooter
        reportData={reportData}
        currentStatus={currentStatus}
        isDownloadLoading={isDownloadLoading}
        isTriggerLoading={isTriggerLoading}
        isPreviewLoading={isPreviewLoading}
        isLaunchLoading={isLaunchLoading}
        triggerError={triggerError}
        triggerConflict={triggerConflict}
        onDownload={handleDownload}
        onRetrigger={handleRetrigger}
        onPreview={handlePreview}
        onLaunch={handleLaunch}
        onClose={handleClose}
        showOnlyClose={currentStatus === 'RUNNING' || isReportLoading}
      />

      <LaunchConfirmModal
        isOpen={launchModal.isOpen}
        blockerCount={launchModal.blockerCount}
        blockerDetails={launchModal.blockerDetails}
        onClose={handleLaunchModalClose}
      />
    </Box>
  );
}

export default CampaignPreLaunch;
